import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  AccessTime,
  AccountBalance,
  Badge,
  CheckCircle,
  Delete,
  Download,
  Draw,
  EventAvailable,
  EventBusy,
  FamilyRestroom,
  Folder,
  Inventory2,
  MedicalServices,
  Person,
  Public,
  Refresh,
  Save,
  School,
  Security,
  UploadFile,
  Visibility,
  VisibilityOff,
  WarningAmber,
  Work,
  WorkspacePremium,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { buildPublicFileUrl } from '../../config/env';
import {
  DetailField,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PageLayout,
  ResponsiveCardList,
  SectionCard,
  StatusBadge,
} from '../../components/ui';
import { useMotionPresets } from '../../theme/motion';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') {
    return (
      value.name ||
      value.full_name ||
      value.title ||
      value.label ||
      value.employee_code ||
      value.code ||
      '-'
    );
  }
  return value;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const initialsOf = (name) =>
  String(name || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const assetStatusTone = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'assigned') return 'active';
  if (value === 'maintenance') return 'pending';
  if (value === 'expired') return 'rejected';
  return status || 'neutral';
};

const listCount = (items) => (Array.isArray(items) ? items.length : 0);

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
};

const expiryToneFor = (days) => {
  if (days === null || days === undefined) return null;
  if (days < 0) return 'error';
  if (days <= 30) return 'error';
  if (days <= 90) return 'warning';
  return null;
};

const tenureLabel = (start) => {
  if (!start) return null;
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  let months = now.getMonth() - startDate.getMonth();
  if (now.getDate() < startDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years <= 0 && months <= 0) return 'New joiner';
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}mo`);
  return parts.join(' ') || 'New joiner';
};

const maskAccount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const text = String(value);
  if (text.length <= 4) return '••••';
  return `•••• •••• ${text.slice(-4)}`;
};

const policyProgress = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const now = Date.now();
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
  const pct = Math.min(100, Math.max(0, ((now - s) / (e - s)) * 100));
  return Math.round(pct);
};

function DetailsGrid({ fields, columns = { xs: 1, sm: 2, lg: 3 } }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${columns.xs || 1}, minmax(0, 1fr))`,
          sm: `repeat(${columns.sm || 2}, minmax(0, 1fr))`,
          lg: `repeat(${columns.lg || 3}, minmax(0, 1fr))`,
        },
        gap: 1,
      }}
    >
      {fields.map((field) => (
        <DetailField key={field.label} compact {...field} />
      ))}
    </Box>
  );
}

function ProfileSummaryCard({ label, value, hint, tone = 'primary', icon, progress }) {
  const theme = useTheme();
  const color = theme.palette[tone]?.main || theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -3 }}
      transition={{ type: 'spring', stiffness: 320, damping: 22 }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: 1.4,
        pl: 1.6,
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: alpha(color, dark ? 0.32 : 0.2),
        background: `linear-gradient(135deg, ${alpha(color, dark ? 0.18 : 0.09)} 0%, ${alpha(
          color,
          dark ? 0.05 : 0.02
        )} 100%)`,
        minWidth: 0,
        cursor: 'default',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: `linear-gradient(180deg, ${color} 0%, ${alpha(color, 0.45)} 100%)`,
        },
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 850, letterSpacing: '0.06em', textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.3, fontWeight: 900, lineHeight: 1.15, color }}>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
        {icon ? (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: theme.hr?.radius?.sm || 1.5,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color,
              bgcolor: alpha(color, dark ? 0.22 : 0.13),
            }}
          >
            {icon}
          </Box>
        ) : null}
      </Stack>
      {typeof progress === 'number' ? (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 1.1,
            height: 5,
            borderRadius: 999,
            bgcolor: alpha(color, dark ? 0.18 : 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
            },
          }}
        />
      ) : null}
    </Box>
  );
}

function ExpiryAlertBanner({ items }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const visible = items.filter((item) => item.tone);
  if (!visible.length) return null;
  const severity = visible.some((item) => item.tone === 'error') ? 'error' : 'warning';
  const accent = theme.palette[severity].main;

  return (
    <Alert
      severity={severity}
      icon={<WarningAmber />}
      sx={{
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: alpha(accent, dark ? 0.4 : 0.28),
        bgcolor: alpha(accent, dark ? 0.14 : 0.07),
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <AlertTitle sx={{ fontWeight: 900, mb: 0.6 }}>Documents need attention</AlertTitle>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {visible.map((item) => {
          const itemColor = theme.palette[item.tone].main;
          const isPast = item.days < 0;
          const label = isPast
            ? `${item.label} expired ${Math.abs(item.days)}d ago`
            : `${item.label} in ${item.days}d`;
          return (
            <Chip
              key={item.label}
              size="small"
              icon={isPast ? <EventBusy fontSize="small" /> : <AccessTime fontSize="small" />}
              label={label}
              sx={{
                fontWeight: 800,
                color: itemColor,
                bgcolor: alpha(itemColor, dark ? 0.22 : 0.12),
                border: '1px solid',
                borderColor: alpha(itemColor, dark ? 0.4 : 0.3),
                '& .MuiChip-icon': { color: itemColor },
              }}
            />
          );
        })}
      </Stack>
    </Alert>
  );
}

function CompletionRing({ value, size = 56, color }) {
  const theme = useTheme();
  const ringColor = color || theme.palette.success.main;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{ color: alpha(ringColor, 0.18), position: 'absolute', inset: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={4}
        sx={{
          color: ringColor,
          position: 'absolute',
          inset: 0,
          '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 900, color: '#fff' }}>
          {value}%
        </Typography>
      </Box>
    </Box>
  );
}

function ProfileIdentity({ user, avatarUrl, passportUrl, onRefresh, completion }) {
  const theme = useTheme();
  const managerName = formatValue(user.manager);
  const tenure = tenureLabel(user.contract_issue_date);
  const identityFields = [
    { label: 'Employee Code', value: user.employee_code, icon: <Badge fontSize="small" /> },
    { label: 'Department', value: user.department, icon: <Work fontSize="small" /> },
    { label: 'Site', value: user.site, icon: <Public fontSize="small" /> },
    { label: 'Manager', value: managerName, icon: <Person fontSize="small" /> },
  ];

  const dotPattern = `radial-gradient(${alpha('#ffffff', 0.16)} 1px, transparent 1px)`;

  return (
    <SectionCard
      tone="dashboard"
      sx={{
        overflow: 'hidden',
        borderRadius: theme.hr?.radius?.lg || 2,
        boxShadow: theme.hr?.shadow?.lift,
      }}
      contentSx={{ p: 0 }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(300px, 0.6fr)' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.4 },
            color: '#ffffff',
            background: theme.hr?.gradient?.hero,
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                `radial-gradient(700px 220px at 5% -10%, ${alpha('#ffffff', 0.22)}, transparent 58%),` +
                `radial-gradient(520px 220px at 100% 0%, ${alpha(theme.palette.success.main, 0.28)}, transparent 62%)`,
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: dotPattern,
              backgroundSize: '18px 18px',
              opacity: 0.5,
              mixBlendMode: 'overlay',
            }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.8}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ position: 'relative' }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: { xs: 78, sm: 92 },
                  height: { xs: 78, sm: 92 },
                  color: theme.palette.primary.main,
                  bgcolor: '#ffffff',
                  fontSize: '2rem',
                  fontWeight: 900,
                  border: `3px solid ${alpha('#ffffff', 0.42)}`,
                  boxShadow: `0 10px 28px ${alpha('#000', 0.28)}`,
                }}
              >
                {initialsOf(user.name)}
              </Avatar>
              <Tooltip title="Active" placement="top" arrow>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: theme.palette.success.main,
                    border: `2.5px solid ${alpha('#ffffff', 0.95)}`,
                    boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.55)}`,
                    animation: 'pulseDot 2s infinite',
                    '@keyframes pulseDot': {
                      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.55)}` },
                      '70%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0)}` },
                      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}` },
                    },
                  }}
                />
              </Tooltip>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#ffffff', 0.82),
                  fontWeight: 850,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Employee account
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  mt: 0.35,
                  color: '#ffffff',
                  fontWeight: 900,
                  lineHeight: 1.05,
                  textShadow: `0 2px 12px ${alpha('#000', 0.18)}`,
                }}
              >
                {formatValue(user.name)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.7, color: alpha('#ffffff', 0.85) }}>
                {formatValue(user.position)} · {formatValue(user.contract_type)}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.3 }}>
                <Chip
                  size="small"
                  label={formatValue(user.role)}
                  sx={{ color: '#ffffff', bgcolor: alpha('#ffffff', 0.18), fontWeight: 800 }}
                />
                <Chip
                  size="small"
                  label={`Code ${formatValue(user.employee_code)}`}
                  sx={{ color: '#ffffff', bgcolor: alpha('#ffffff', 0.16), fontWeight: 800 }}
                />
                {tenure ? (
                  <Chip
                    size="small"
                    icon={<AccessTime fontSize="small" sx={{ color: '#fff !important' }} />}
                    label={tenure}
                    sx={{
                      color: '#ffffff',
                      bgcolor: alpha(theme.palette.success.main, 0.32),
                      fontWeight: 800,
                      border: `1px solid ${alpha('#fff', 0.22)}`,
                    }}
                  />
                ) : null}
              </Stack>
            </Box>

            <Stack
              direction={{ xs: 'row', sm: 'column', md: 'row' }}
              spacing={1}
              alignItems="center"
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {typeof completion === 'number' ? (
                <CompletionRing value={completion} />
              ) : null}
              <Stack direction="row" spacing={0.8}>
                {passportUrl ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download />}
                    href={passportUrl}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      color: '#ffffff',
                      borderColor: alpha('#ffffff', 0.36),
                      backdropFilter: 'blur(6px)',
                      bgcolor: alpha('#ffffff', 0.06),
                      '&:hover': {
                        borderColor: alpha('#ffffff', 0.55),
                        bgcolor: alpha('#ffffff', 0.14),
                      },
                    }}
                  >
                    Passport
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={onRefresh}
                  sx={{
                    color: '#ffffff',
                    borderColor: alpha('#ffffff', 0.36),
                    backdropFilter: 'blur(6px)',
                    bgcolor: alpha('#ffffff', 0.06),
                    '&:hover': {
                      borderColor: alpha('#ffffff', 0.55),
                      bgcolor: alpha('#ffffff', 0.14),
                    },
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.6, sm: 2 } }}>
          <Stack spacing={1.1}>
            {identityFields.map((field) => (
              <Stack
                key={field.label}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.2}
                sx={{
                  py: 0.4,
                  borderBottom: '1px dashed',
                  borderColor: theme.hr?.line?.soft || 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      color: 'primary.main',
                      display: 'grid',
                      placeItems: 'center',
                      width: 22,
                      height: 22,
                    }}
                  >
                    {field.icon}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    {field.label}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800, textAlign: 'right', overflowWrap: 'anywhere' }}
                >
                  {formatValue(field.value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    </SectionCard>
  );
}

function AssetsPanel({ assets }) {
  return (
    <SectionCard tone="dashboard" title="Assigned Assets" subheader="Assets currently linked to this employee." disablePadding>
      <ResponsiveCardList
        items={assets}
        emptyTitle="No assets assigned"
        desktop={
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Serial</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id || asset.asset_tag}>
                    <TableCell>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontWeight: 900 }}>
                          {String(asset.asset_type || asset.asset_tag || 'A')[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatValue(asset.asset_tag)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatValue(asset.asset_type)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatValue(asset.serial_number)}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={formatValue(asset.condition)} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={assetStatusTone(asset.status)} label={formatValue(asset.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
        renderCard={(asset) => (
          <DetailField
            label={formatValue(asset.asset_type)}
            value={formatValue(asset.asset_tag)}
            icon={<Work fontSize="small" />}
            action={<StatusBadge status={assetStatusTone(asset.status)} label={formatValue(asset.status)} />}
          />
        )}
      />
    </SectionCard>
  );
}

function SignaturePanel({ user, onUpdated }) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState(user?.signature_title || '');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const currentUrl = buildPublicFileUrl(user?.signature_url || user?.signature_path);

  // Revoke object URLs on unmount / change to avoid memory leaks.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleSelect = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(selected.type)) {
      toast.error('Please choose a PNG, JPG, or WEBP image.');
      return;
    }
    if (selected.size > 2 * 1024 * 1024) {
      toast.error('Signature image must be 2MB or smaller.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const resetSelection = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!file) {
      fileInputRef.current?.click();
      return;
    }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('signature', file);
      if (title.trim()) fd.append('signature_title', title.trim());
      await api.post('/users/signature', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signature saved.');
      resetSelection();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save signature.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      await api.delete('/users/signature');
      toast.success('Signature removed.');
      resetSelection();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove signature.');
    } finally {
      setRemoving(false);
    }
  };

  const shownUrl = previewUrl || currentUrl;
  const busy = saving || removing;

  return (
    <SectionCard
      tone="dashboard"
      title="Approval Signature"
      subheader="Upload your handwritten signature image. It is embedded on job offers and approval documents you sign off."
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 150,
            p: 2,
            borderRadius: theme.hr?.radius?.md || 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: '#ffffff',
          }}
        >
          {shownUrl ? (
            <Box
              component="img"
              src={shownUrl}
              alt="Signature preview"
              sx={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              <Draw />
              <Typography variant="body2">No signature uploaded yet.</Typography>
            </Stack>
          )}
        </Box>

        <TextField
          label="Signature title (optional)"
          placeholder="e.g. Human Resources Manager"
          size="small"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={handleSelect}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {shownUrl ? 'Choose different image' : 'Choose image'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!file || busy}
          >
            Save signature
          </Button>
          {file ? (
            <Button color="inherit" onClick={resetSelection} disabled={busy}>
              Cancel
            </Button>
          ) : null}
          {currentUrl && !file ? (
            <Button
              color="error"
              variant="outlined"
              startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <Delete />}
              onClick={handleRemove}
              disabled={busy}
            >
              Remove
            </Button>
          ) : null}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          PNG with a transparent background looks best on documents. Max size 2MB.
        </Typography>
      </Stack>
    </SectionCard>
  );
}

/**
 * Two-step verification, self-service.
 *
 * The toggle is hidden entirely when the workspace has not switched two-step
 * on — there is nothing meaningful to offer, and showing a dead switch invites
 * people to believe they have protection they do not have. When a role is not
 * allowed to opt out, the switch renders disabled with the reason, rather than
 * silently failing on save.
 */
function SecurityPanel() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/mfa-preference');
      setState(res.data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (next) => {
    setSaving(true);
    // Optimistic, then reconciled with the server's answer — the server is the
    // authority on whether this change was allowed at all.
    setState((s) => ({ ...s, enabled: next }));
    try {
      const res = await api.put('/users/mfa-preference', { enabled: next });
      setState(res.data);
      toast.success(next ? 'Two-step verification is on.' : 'Two-step verification is off.');
    } catch (err) {
      setState((s) => ({ ...s, enabled: !next }));
      toast.error(err?.response?.data?.message || 'Could not change that setting.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SectionCard tone="dashboard" title="Security">
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={22} />
        </Stack>
      </SectionCard>
    );
  }

  if (!state?.available) {
    return (
      <SectionCard
        tone="dashboard"
        title="Two-Step Verification"
        subheader="Not enabled for this workspace."
      >
        <Alert severity="info">
          Two-step verification has not been switched on for your organization. There is nothing to
          configure here yet.
        </Alert>
      </SectionCard>
    );
  }

  const locked = !state.can_change;

  return (
    <SectionCard
      tone="dashboard"
      title="Two-Step Verification"
      subheader="Ask for a one-time code emailed to you when signing in."
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 700 }}>
                {state.enabled ? 'On' : 'Off'}
              </Typography>
              {locked ? <Chip size="small" label="Required" color="primary" /> : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {state.delivery_hint
                ? `Codes are sent to ${state.delivery_hint}.`
                : 'No email address on file — contact HR.'}
            </Typography>
          </Box>
          <Switch
            checked={Boolean(state.enabled)}
            disabled={locked || saving || !state.has_email}
            onChange={(e) => handleToggle(e.target.checked)}
            inputProps={{ 'aria-label': 'Two-step verification' }}
          />
        </Stack>

        {locked ? (
          <Alert severity="info">{state.locked_reason}</Alert>
        ) : null}

        {!state.has_email ? (
          <Alert severity="warning">
            Your record has no email address, so a code cannot reach you. Contact HR to add one.
          </Alert>
        ) : null}

        {state.web_only ? (
          <Alert severity="warning">
            <AlertTitle>Website only</AlertTitle>
            This applies when you sign in on the website. The mobile app does not ask for a code
            yet, so it is not protected by this setting.
          </Alert>
        ) : null}

        <Typography variant="body2" color="text.secondary">
          Tick <em>Trust this device</em> when you enter a code and that browser will not ask again
          for {state.trusted_device_days} days. Nobody from HR or IT will ever ask you to read a
          code out — if someone does, they are not from this company.
        </Typography>
      </Stack>
    </SectionCard>
  );
}

const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Unknown';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'Just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
  if (secs < 2592000) return `${Math.floor(secs / 86400)} days ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * "Where am I signed in?" — the point of this list is recognition, so it leads
 * with the device and when it was last used. The IP is shown raw and is never
 * geolocated; nothing about these sessions is sent anywhere to be resolved
 * into a place.
 */
function SessionsPanel() {
  const [data, setData] = useState({ sessions: [], trusted_devices: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/sessions');
      setData(res.data || { sessions: [], trusted_devices: [] });
    } catch {
      setData({ sessions: [], trusted_devices: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (sessionId) => {
    setBusy(sessionId);
    try {
      const res = await api.delete(`/users/sessions/${sessionId}`);
      setData((d) => ({ ...d, sessions: res.data.sessions }));
      toast.success(res.data.message || 'Signed out.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not sign that device out.');
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async () => {
    setBusy('all');
    try {
      const res = await api.delete('/users/sessions');
      setData((d) => ({ ...d, sessions: res.data.sessions }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not sign the other devices out.');
    } finally {
      setBusy(null);
    }
  };

  const forgetDevice = async (id) => {
    setBusy(`td-${id}`);
    try {
      const res = await api.delete(`/users/trusted-devices/${id}`);
      setData((d) => ({ ...d, trusted_devices: res.data.trusted_devices }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not forget that device.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <SectionCard tone="dashboard" title="Where You're Signed In">
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={22} />
        </Stack>
      </SectionCard>
    );
  }

  const others = data.sessions.filter((s) => !s.is_current);

  return (
    <Stack spacing={2}>
      <SectionCard
        tone="dashboard"
        title="Where You're Signed In"
        subheader="Devices currently signed in to your account. Sign out anything you don't recognise."
        action={
          others.length ? (
            <Button size="small" variant="outlined" disabled={busy === 'all'} onClick={revokeOthers}>
              Sign out others
            </Button>
          ) : null
        }
      >
        <Stack spacing={1.2}>
          {data.sessions.length === 0 ? (
            <Alert severity="info">No active sessions recorded yet.</Alert>
          ) : null}

          {data.sessions.map((s) => (
            <Box
              key={s.session_id}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: s.is_current ? 'primary.main' : 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ fontWeight: 700 }}>{s.device_label}</Typography>
                    {s.is_current ? <Chip size="small" color="primary" label="This device" /> : null}
                    {s.mfa_verified ? <Chip size="small" variant="outlined" label="Code verified" /> : null}
                    {s.trusted_device ? <Chip size="small" variant="outlined" label="Trusted device" /> : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    IP {s.ip || 'unknown'} · Last active {relativeTime(s.last_seen_at)}
                  </Typography>
                  {s.ip_changed ? (
                    <Typography variant="caption" color="warning.main">
                      Address changed since sign-in (was {s.signed_in_ip})
                    </Typography>
                  ) : null}
                </Box>
                {!s.is_current ? (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={busy === s.session_id}
                    onClick={() => revoke(s.session_id)}
                  >
                    Sign out
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ))}
        </Stack>
      </SectionCard>

      {data.trusted_devices.length ? (
        <SectionCard
          tone="dashboard"
          title="Trusted Browsers"
          subheader="These skip the sign-in code until they expire. Forget any you no longer use."
        >
          <Stack spacing={1.2}>
            {data.trusted_devices.map((d) => (
              <Stack
                key={d.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{d.device_label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    IP {d.ip || 'unknown'} · Last used {relativeTime(d.last_used_at)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={busy === `td-${d.id}`}
                  onClick={() => forgetDevice(d.id)}
                >
                  Forget
                </Button>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      ) : null}
    </Stack>
  );
}

export default function Profile() {
  const theme = useTheme();
  const motionPresets = useMotionPresets();

  const [user, setUser] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [revealBank, setRevealBank] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const userRes = await api.get('/users');
      setUser(userRes.data);
      setAssets(Array.isArray(userRes.data?.assets) ? userRes.data.assets : []);
    } catch (err) {
      setError('Failed to load profile data');
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const avatarUrl = buildPublicFileUrl(user?.profile_image_url || user?.profile_image_path);
  const passportUrl = buildPublicFileUrl(user?.passport_image_url || user?.passport_image_path);

  const profileFields = useMemo(
    () => [
      { label: 'Employee Code', value: user?.employee_code, icon: <AccountBalance fontSize="small" /> },
      { label: 'Position', value: user?.position, icon: <Work fontSize="small" /> },
      { label: 'Department', value: user?.department, icon: <Work fontSize="small" /> },
      { label: 'Site', value: user?.site, icon: <Public fontSize="small" /> },
      { label: 'Nationality', value: user?.nationality, icon: <Public fontSize="small" /> },
      { label: 'Contract Type', value: user?.contract_type, icon: <Badge fontSize="small" /> },
      { label: 'Contract Issued Date', value: formatDate(user?.contract_issue_date), icon: <Badge fontSize="small" /> },
      { label: 'Contract Expiry Date', value: formatDate(user?.contract_expiry_date), icon: <Badge fontSize="small" /> },
      { label: 'Iqama No.', value: user?.iqama_no, icon: <Badge fontSize="small" /> },
      { label: 'Passport No.', value: user?.passport_no, icon: <Badge fontSize="small" /> },
      { label: 'Passport Issued Date', value: formatDate(user?.passport_issue_date), icon: <Badge fontSize="small" /> },
      { label: 'Passport Expiry Date', value: formatDate(user?.passport_expiry_date), icon: <Badge fontSize="small" /> },
      { label: 'Manager', value: formatValue(user?.manager), icon: <Person fontSize="small" /> },
    ],
    [user]
  );

  const bankFields = useMemo(
    () => [
      { label: 'Bank Name', value: user?.bank_name, icon: <AccountBalance fontSize="small" /> },
      {
        label: 'Account Number',
        value: revealBank ? user?.account_number : maskAccount(user?.account_number),
        icon: <AccountBalance fontSize="small" />,
      },
      {
        label: 'IBAN',
        value: revealBank ? user?.iban : maskAccount(user?.iban),
        icon: <AccountBalance fontSize="small" />,
      },
    ],
    [user, revealBank]
  );

  const familyMembers = Array.isArray(user?.family_members) ? user.family_members : [];
  const insurancePolicies = Array.isArray(user?.assigned_policies) ? user.assigned_policies : [];
  const certificates = Array.isArray(user?.certificates) ? user.certificates : [];
  const profileCompletion = profileFields.length
    ? Math.round((profileFields.filter((field) => formatValue(field.value) !== '-').length / profileFields.length) * 100)
    : 0;

  const expiryItems = useMemo(
    () => [
      { label: 'Iqama', days: daysUntil(user?.iqama_expiry_date), tone: null },
      { label: 'Passport', days: daysUntil(user?.passport_expiry_date), tone: null },
      { label: 'Contract', days: daysUntil(user?.contract_expiry_date), tone: null },
    ].map((item) => ({ ...item, tone: expiryToneFor(item.days) })),
    [user]
  );

  if (loading) {
    return (
      <PageLayout backgroundAccent compact maxWidth="xl">
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout backgroundAccent compact maxWidth="lg">
        <ErrorState title="Profile" description={error} onRetry={fetchData} />
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout backgroundAccent compact maxWidth="lg">
        <EmptyState title="No profile data" description="No user data is available for this account." />
      </PageLayout>
    );
  }

  const tabSx = {
    minHeight: 42,
    px: 1.3,
    fontWeight: 850,
    '& .MuiTab-iconWrapper': { mr: 0.65 },
  };

  const tabLabelWithCount = (label, count) => (
    <Stack direction="row" spacing={0.7} alignItems="center">
      <span>{label}</span>
      {count > 0 ? (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 18,
            px: 0.65,
            fontSize: 11,
            fontWeight: 900,
            lineHeight: 1,
            borderRadius: 999,
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          }}
        >
          {count}
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        overline="Account"
        title="Profile"
        subtitle="Identity, payroll details, assets, family, insurance, and certificates in one compact workspace."
      />

      <Stack spacing={1.5}>
        <ProfileIdentity
          user={user}
          avatarUrl={avatarUrl}
          passportUrl={passportUrl}
          onRefresh={fetchData}
          completion={profileCompletion}
        />

        <ExpiryAlertBanner items={expiryItems} />

        <Box
          component={motion.div}
          variants={motionPresets.stagger.container}
          initial="initial"
          animate="animate"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          <Box component={motion.div} variants={motionPresets.stagger.item}>
            <ProfileSummaryCard
              label="Completion"
              value={`${profileCompletion}%`}
              hint="Core profile fields"
              tone="success"
              icon={<CheckCircle />}
              progress={profileCompletion}
            />
          </Box>
          <Box component={motion.div} variants={motionPresets.stagger.item}>
            <ProfileSummaryCard
              label="Assets"
              value={listCount(assets)}
              hint="Assigned items"
              tone="primary"
              icon={<Inventory2 />}
            />
          </Box>
          <Box component={motion.div} variants={motionPresets.stagger.item}>
            <ProfileSummaryCard
              label="Family"
              value={listCount(familyMembers)}
              hint="Registered members"
              tone="secondary"
              icon={<FamilyRestroom />}
            />
          </Box>
          <Box component={motion.div} variants={motionPresets.stagger.item}>
            <ProfileSummaryCard
              label="Documents"
              value={listCount(certificates)}
              hint="Certificates on file"
              tone="info"
              icon={<Folder />}
            />
          </Box>
        </Box>

        <SectionCard tone="dashboard" disablePadding sx={{ borderRadius: theme.hr?.radius?.lg || 2 }}>
          <Box sx={{ px: 1, pt: 1 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
              <Tab sx={tabSx} icon={<Person fontSize="small" />} iconPosition="start" label="Details" />
              <Tab sx={tabSx} icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Bank" />
              <Tab
                sx={tabSx}
                icon={<Work fontSize="small" />}
                iconPosition="start"
                label={tabLabelWithCount('Assets', listCount(assets))}
              />
              <Tab
                sx={tabSx}
                icon={<FamilyRestroom fontSize="small" />}
                iconPosition="start"
                label={tabLabelWithCount('Family', listCount(familyMembers))}
              />
              <Tab
                sx={tabSx}
                icon={<MedicalServices fontSize="small" />}
                iconPosition="start"
                label={tabLabelWithCount('Insurance', listCount(insurancePolicies))}
              />
              <Tab
                sx={tabSx}
                icon={<School fontSize="small" />}
                iconPosition="start"
                label={tabLabelWithCount('Certificates', listCount(certificates))}
              />
              <Tab sx={tabSx} icon={<Draw fontSize="small" />} iconPosition="start" label="Signature" />
              <Tab sx={tabSx} icon={<Security fontSize="small" />} iconPosition="start" label="Security" />
            </Tabs>
          </Box>
          <Divider />

          <Box sx={{ p: { xs: 1.2, sm: 1.5 } }}>
            {tab === 0 ? (
              <SectionCard
                tone="dashboard"
                title="Employee Details"
                subheader="Core employment and official document information."
                contentSx={{ p: { xs: 1.2, sm: 1.5 } }}
              >
                <DetailsGrid fields={profileFields} />
              </SectionCard>
            ) : null}

            {tab === 1 ? (
              <SectionCard
                tone="dashboard"
                title="Bank Information"
                subheader="Payroll banking details linked to the employee record."
                action={
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={revealBank ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    onClick={() => setRevealBank((value) => !value)}
                  >
                    {revealBank ? 'Hide' : 'Reveal'}
                  </Button>
                }
                contentSx={{ p: { xs: 1.2, sm: 1.5 } }}
              >
                <DetailsGrid fields={bankFields} columns={{ xs: 1, sm: 2, lg: 3 }} />
              </SectionCard>
            ) : null}

            {tab === 2 ? <AssetsPanel assets={assets} /> : null}

            {tab === 3 ? (
              <SectionCard tone="dashboard" title="Family Members" subheader="Registered family members attached to the employee profile.">
                {familyMembers.length ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1,
                    }}
                  >
                    {familyMembers.map((member) => (
                      <DetailField
                        key={member.id || `${member.name}-${member.relationship}`}
                        compact
                        label={formatValue(member.relationship)}
                        value={formatValue(member.name)}
                        icon={<FamilyRestroom fontSize="small" />}
                        action={member.birth_date ? <Chip size="small" label={`DOB ${formatDate(member.birth_date)}`} /> : null}
                      />
                    ))}
                  </Box>
                ) : (
                  <EmptyState compact title="No family members registered" />
                )}
              </SectionCard>
            ) : null}

            {tab === 4 ? (
              <SectionCard tone="dashboard" title="Insurance Policies" subheader="Current insurance policy assignments.">
                {insurancePolicies.length ? (
                  <Stack spacing={1.1}>
                    {insurancePolicies.map((policy) => {
                      const plan = policy.insurance_policy || {};
                      const active = policy.end_date ? new Date(policy.end_date) > new Date() : false;
                      const progress = policyProgress(policy.start_date, policy.end_date);
                      const remainingDays = daysUntil(policy.end_date);
                      const remainingTone =
                        remainingDays === null
                          ? 'text.secondary'
                          : remainingDays < 0
                            ? theme.palette.error.main
                            : remainingDays <= 30
                              ? theme.palette.warning.main
                              : theme.palette.success.main;
                      const accent = active ? theme.palette.success.main : theme.palette.error.main;
                      return (
                        <Box
                          key={policy.id || `${plan.name}-${policy.start_date}`}
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            p: 1.4,
                            pl: 1.7,
                            borderRadius: theme.hr?.radius?.md || 2,
                            border: '1px solid',
                            borderColor: alpha(accent, theme.palette.mode === 'dark' ? 0.32 : 0.2),
                            bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.08 : 0.03),
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              background: `linear-gradient(180deg, ${accent} 0%, ${alpha(accent, 0.4)} 100%)`,
                            },
                          }}
                        >
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: theme.hr?.radius?.sm || 1.5,
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: accent,
                                  bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.22 : 0.13),
                                  flexShrink: 0,
                                }}
                              >
                                <WorkspacePremium fontSize="small" />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                  {formatValue(plan.name)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatValue(plan.type)}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {remainingDays !== null && active ? (
                                <Chip
                                  size="small"
                                  icon={<EventAvailable fontSize="small" />}
                                  label={`${remainingDays}d left`}
                                  sx={{
                                    fontWeight: 800,
                                    color: remainingTone,
                                    bgcolor: alpha(remainingTone, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                                    '& .MuiChip-icon': { color: remainingTone },
                                  }}
                                />
                              ) : null}
                              <StatusBadge status={active ? 'active' : 'inactive'} label={active ? 'Active' : 'Expired'} />
                            </Stack>
                          </Stack>

                          {progress !== null ? (
                            <Box sx={{ mt: 1.2 }}>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                  {formatDate(policy.start_date)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                  {formatDate(policy.end_date)}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: alpha(accent, 0.1),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.55)} 100%)`,
                                  },
                                }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ mt: 1 }}>
                              <DetailsGrid
                                fields={[
                                  { label: 'Start Date', value: formatDate(policy.start_date) },
                                  { label: 'End Date', value: formatDate(policy.end_date) },
                                  { label: 'Policy Type', value: plan.type },
                                ]}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <EmptyState compact title="No insurance policies assigned" />
                )}
              </SectionCard>
            ) : null}

            {tab === 5 ? (
              <SectionCard tone="dashboard" title="Certificates" subheader="Uploaded certificates and employee documents.">
                {certificates.length ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                      gap: 1,
                    }}
                  >
                    {certificates.map((cert) => {
                      const fileUrl = buildPublicFileUrl(cert.file_path);
                      return (
                        <DetailField
                          key={cert.id || cert.title}
                          compact
                          label={formatDate(cert.issue_date)}
                          value={formatValue(cert.title)}
                          icon={<School fontSize="small" />}
                          action={
                            fileUrl ? (
                              <Button size="small" variant="outlined" startIcon={<Download />} href={fileUrl} target="_blank" rel="noreferrer">
                                Open
                              </Button>
                            ) : null
                          }
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <EmptyState compact title="No certificates available" />
                )}
              </SectionCard>
            ) : null}

            {tab === 6 ? <SignaturePanel user={user} onUpdated={fetchData} /> : null}

            {tab === 7 ? (
              <Stack spacing={2}>
                <SecurityPanel />
                <SessionsPanel />
              </Stack>
            ) : null}
          </Box>
        </SectionCard>
      </Stack>
    </PageLayout>
  );
}
